import { useEffect, useRef, useState } from "react";

const TICK_INTERVAL_MS = 300;
const COMPLETION_DISPLAY_MS = 2000;
const MIN_DISPLAY_MS = 5000;
const MAX_SIMULATED_PROGRESS = 90;

interface SimulatedProgressResult {
  progress: number;
  isVisible: boolean;
  isCompleted: boolean;
  hasFailed: boolean;
}

/**
 * Produces a simulated progress value (0-100) that climbs with
 * logarithmic slowdown while `isActive` is true and jumps to
 * 100 once `isActive` flips back to false. The finished state stays up for
 * `completionDisplayMs` before the banner hides.
 */
const useSimulatedProgress = (
  isActive: boolean,
  hasFailed = false,
  completionDisplayMs = COMPLETION_DISPLAY_MS,
): SimulatedProgressResult => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [failed, setFailed] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevActiveRef = useRef(false);
  const startedAtRef = useRef(0);

  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = isActive;

    if (isActive && !wasActive) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }

      setProgress(0);
      setIsCompleted(false);
      setFailed(false);
      setIsVisible(true);
      startedAtRef.current = Date.now();

      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= MAX_SIMULATED_PROGRESS) return prev;
          const remaining = MAX_SIMULATED_PROGRESS - prev;
          const increment = Math.max(0.5, remaining * 0.05);
          return Math.min(prev + increment, MAX_SIMULATED_PROGRESS);
        });
      }, TICK_INTERVAL_MS);
    } else if (!isActive && wasActive) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      const elapsed = Date.now() - startedAtRef.current;
      const delay = Math.max(0, MIN_DISPLAY_MS - elapsed);

      const finishCompletion = () => {
        setProgress(100);
        setIsCompleted(true);
        setFailed(hasFailed);

        hideTimerRef.current = setTimeout(() => {
          setIsVisible(false);
          setProgress(0);
          setIsCompleted(false);
          setFailed(false);
        }, completionDisplayMs);
      };

      if (delay > 0) {
        completionTimerRef.current = setTimeout(finishCompletion, delay);
      } else {
        finishCompletion();
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, hasFailed, completionDisplayMs]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    };
  }, []);

  return { progress, isVisible, isCompleted, hasFailed: failed };
};

export default useSimulatedProgress;
